import { Button, ButtonGroup, Link, TextField, Tooltip } from "@shopify/polaris"
import { ClipboardIcon, ViewIcon } from "@shopify/polaris-icons"
import classNames from "classnames"
import copy from "copy-to-clipboard"
import { observer } from "mobx-react"
import { useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useIntercom } from "react-use-intercom"

import ShopifyGraphql from "@/api/shopify-graphql/shopify-graphql"
import { Menu, MenuItem } from "@/api/shopify-graphql/shopify-graphql.type"
import AddTrackingPageModal from "@/pages/Dashboard/components/AddTrackingPageModal"
import ReplaceTrackingPageModal from "@/pages/Dashboard/components/MigrateSetupGuide/components/ReplaceTrackingPageModal"
import { checkMenuItemsByUrl, getShortTrackLink } from "@/pages/TrackingPage/utils"
import stores from "@/stores"
import { openLink } from "@/utils/redirectShopify"
import toast from "@/utils/toast"

import MigrateSetupGuideTabContent from "../components/MigrateSetupGuideTabContent"
import styles from "../index.module.scss"
import { MigrateSetupGuideTabProps } from "./types"

const TrackingPageTab = observer(({
  tmsMigration,
  onCompleteStep,
  isCompleted,
}: MigrateSetupGuideTabProps) => {
  const { t } = useTranslation(["dashboard", "common"])
  const { showNewMessage } = useIntercom()
  const [addTrackingPageShow, setAddTrackingPageShow] = useState(false)
  const [replaceTrackingPageShow, setReplaceTrackingPageShow] = useState(false)
  const [isCheckingMenu, setIsCheckingMenu] = useState(false)
  const [isReplacingMenu, setIsReplacingMenu] = useState(false)
  const [submittingAction, setSubmittingAction] = useState<"replace" | "manual" | null>(null)
  const isSubmitting = submittingAction !== null
  // 左按钮（替换/检测/添加完成）正在进行
  const isReplaceBusy = isCheckingMenu || isReplacingMenu || submittingAction === "replace"
  // 右按钮（手动完成）正在进行
  const isManualBusy = submittingAction === "manual"
  const [menusToUpdate, setMenusToUpdate] = useState<Array<{
    menu: Menu
    updatedItems: MenuItem[]
  }>>([])
  const { userInfoSore } = stores
  const trackUrl = userInfoSore.data.last_track_url
  const parcelWillTrackUrl = getShortTrackLink(trackUrl ?? "")
  const trackingMoreProxyUrl = tmsMigration?.tracking_page?.from ?? ""
  const parcelPanelProxyUrl = useMemo(
    () => tmsMigration?.tracking_page?.to || parcelWillTrackUrl,
    [tmsMigration?.tracking_page?.to, parcelWillTrackUrl],
  )

  const handleCopyLink = () => {
    if (!trackUrl) {
      return
    }

    copy(parcelWillTrackUrl)
    toast(t("common:CopiedSuccessfully"), 2e3)
  }

  const handleCompleteStep = async (action: "replace" | "manual") => {
    if (!onCompleteStep || isSubmitting) {
      return false
    }

    setSubmittingAction(action)

    try {
      return await onCompleteStep(1)
    } finally {
      setSubmittingAction(null)
    }
  }

  const handleReplaceTrackingPageClick = async () => {
    if (!trackingMoreProxyUrl) {
      toast(t("dashboard:ReplaceTrackingPageFailed"), 2e3, true)
      return
    }

    setIsCheckingMenu(true)

    try {
      const menuCheckResult = await checkMenuItemsByUrl(
        trackingMoreProxyUrl,
        parcelPanelProxyUrl,
      )

      const { itemsCount, menusToUpdate: matchedMenusToUpdate } = menuCheckResult

      if (itemsCount === 0) {
        setAddTrackingPageShow(true)
        return
      }

      setMenusToUpdate(matchedMenusToUpdate)
      setReplaceTrackingPageShow(true)
    } catch (error) {
      console.error("Check TrackingMore menu items failed:", error)
      toast(t("dashboard:ReplaceTrackingPageFailed"), 2e3, true)
    } finally {
      setIsCheckingMenu(false)
    }
  }

  const handleReplaceTrackingPage = async () => {
    if (menusToUpdate.length === 0) {
      toast(t("dashboard:ReplaceTrackingPageFailed"), 2e3, true)
      return
    }

    setIsReplacingMenu(true)

    try {
      const updatePromises = menusToUpdate.map(({ menu, updatedItems }) =>
        ShopifyGraphql.setMenu(menu.id, menu.title, updatedItems),
      )
      const responses = await Promise.all(updatePromises)
      const hasFailedResponse = responses.some(response => response.status !== 200)

      if (hasFailedResponse) {
        toast(t("dashboard:ReplaceTrackingPageFailed"), 2e3, true)
        return
      }

      toast(t("dashboard:ReplaceTrackingPageSuccess"), 2e3)
      setReplaceTrackingPageShow(false)
      setMenusToUpdate([])

      await handleCompleteStep("replace")
    } catch (error) {
      console.error("Replace TrackingMore menu items failed:", error)
      toast(t("dashboard:ReplaceTrackingPageFailed"), 2e3, true)
    } finally {
      setIsReplacingMenu(false)
    }
  }

  const handleAddTrackingPageSuccess = async () => {
    await handleCompleteStep("replace")
  }

  return (
    <MigrateSetupGuideTabContent
      header={t("dashboard:ReplaceTrackingPage")}
      media={<img src={"https://cdn.parcelpanel.com/front-end/2026/tms-migration-v07/tracking-page-replace.svg"} alt={""} />}
      description={
        <div className={"flex flex-col gap-1"}>
          {t("dashboard:ReplaceTrackingPageDescription")}
          <div className={classNames("flex flex-wrap gap-[var(--p-space-button-group-gap)] items-center", styles.trackingReplaceWrapper)}>
            <div className={classNames(styles.disableInput, "w-[400px] max-w-full")}>
              <TextField
                value={`https://${trackUrl}`}
                disabled
                readOnly
                autoComplete={"off"}
                label={""}
              />
            </div>

            <Tooltip content={t("dashboard:ViewTrackingPage")}>
              <Button icon={ViewIcon} onClick={() => openLink(`https://${trackUrl}`)} />
            </Tooltip>

            <Tooltip content={t("common:CopyLink")}>
              <Button icon={ClipboardIcon} onClick={handleCopyLink} />
            </Tooltip>
          </div>
        </div>
      }
      actions={
        <div className={"flex flex-col gap-3"}>
          <div className={"flex items-center"}>
            <AddTrackingPageModal
              show={addTrackingPageShow}
              onClose={() => setAddTrackingPageShow(false)}
              trackURL={parcelPanelProxyUrl}
              onSuccess={handleAddTrackingPageSuccess}
            />
            <ReplaceTrackingPageModal
              open={replaceTrackingPageShow}
              loading={isReplacingMenu}
              onClose={() => setReplaceTrackingPageShow(false)}
              onReplace={handleReplaceTrackingPage}
            />
            <ButtonGroup>
              <Button
                variant={isCompleted ? "secondary" : "primary"}
                loading={isReplaceBusy}
                disabled={isManualBusy}
                onClick={handleReplaceTrackingPageClick}
              >
                {t("dashboard:ReplaceTrackingPage")}
              </Button>
              {!isCompleted && (
                <Button
                  variant={"tertiary"}
                  onClick={() => handleCompleteStep("manual")}
                  loading={isManualBusy}
                  disabled={isReplaceBusy}
                >
                  {t("dashboard:ReplaceTrackingPageManualButton")}
                </Button>
              )}
            </ButtonGroup>
          </div>
        </div>
      }
      footer={
        <Trans ns={"dashboard"} i18nKey={"NeedHelp"}>
          <Link onClick={() => showNewMessage("")} removeUnderline />
        </Trans>
      }
    />
  )
})

export default TrackingPageTab
